<template>
    <b-navbar transparent>
        <template #brand>
            <b-navbar-item class="navbar-brand" tag="router-link" :to="{ path: '/' }">
                <img
                    src="@/assets/images/brand/logo-icon.svg"
                    alt="Gamebot Logo"
                >
            </b-navbar-item>
        </template>

        <template #start>
            <b-navbar-item class="has-text-white" tag="router-link" :to="{ path: '/' }">
                Home
            </b-navbar-item>
            <b-navbar-item class="has-text-white" tag="router-link" :to="{ path: '/docs' }">
                Docs
            </b-navbar-item>
            <b-navbar-item  class="has-text-white" tag="router-link" :to="{ path: '/shop' }">
                Shop
            </b-navbar-item>
            <!-- /leaderboard still exists (linkable directly, e.g. from the
                 Server Pass section) but isn't in the nav or robots.txt yet —
                 it only ranks globally today, not per server, which is the
                 real feature the Server Pass sells. -->
            <b-navbar-item  class="has-text-white" tag="router-link" :to="{ path: '/blog' }">
                <!-- <b-button type="is-warning" inverted> -->
                    Blog<b-tag rounded class="tag is-small" type="is-danger" v-if="Date.now() < 1682002316000">NEW!</b-tag>
                <!-- </b-button> -->
            </b-navbar-item>
            <!-- The one nav item that sells something, so it's the one that
                 gets a border. Gold outline, white label, white crown — the
                 same three parts as the wordmark, at nav size. Last in the
                 group rather than mid-row: a pill sitting between plain text
                 links broke the row into two halves. -->
            <b-navbar-item class="has-text-white premium-nav-item" tag="router-link" :to="{ path: '/premium' }">
                <span class="premium-pill">
                    <img
                        class="premium-pill-crown"
                        src="@/assets/images/premium/crown-white.svg"
                        alt="" />
                    Premium
                </span>
            </b-navbar-item>
        </template>

        <template #end>
            <b-navbar-item>
                <nav-balance v-if="$store.getters.getToken && $route.path === '/shop'" :credits="$store.state.dbInfo.credits" :gold="$store.state.dbInfo.gold" />
                <div v-if="!$store.getters.getToken" class="buttons">
                    <b-button tag="router-link" icon-left="discord" icon-pack="fab" type="is-discord" inverted :to="{ path: '/login' }">
                        Log in with Discord
                    </b-button>
                </div>
                <!-- Bulma's `has-addons` joins the pair into one control: it
                     strips the margin between them and squares off the inner
                     corners, so this stays the same button it was, just split.
                     The name navigates to your profile; the three dots hold
                     the actions.

                     Deliberately NOT inside the `.buttons` wrapper above.
                     `.buttons` is Bulma's "list of buttons" primitive and puts
                     a 0.5rem bottom margin on every button it contains, for
                     spacing rows that wrap. Nested inside it, this pair sat
                     40px tall in a 48px box with dead space underneath. -->
                <div v-else class="field has-addons user-menu">
                    <p class="control">
                        <b-button
                            tag="router-link"
                            to="/profile/me"
                            type="is-discord"
                            class="user-link"
                            :title="`${getUserTag} — view your profile`">
                            <!-- Own wrapper rather than styling Buefy's:
                                 b-button puts slot content inside a <span> of
                                 its own, and laying the avatar and name out
                                 from the button meant they were never flex
                                 siblings — they stacked. -->
                            <span class="user-link-inner">
                                <img
                                    v-if="$store.state.user.avatar"
                                    class="user-link-avatar"
                                    :src="$store.state.user.avatar"
                                    :alt="getUserTag" />
                                <span class="user-link-name">{{ getUserTag }}</span>
                                <!-- Same mark as the profile page's crown, in
                                     white version, plain, so it reads on the
                                     dark bar the way the gold one reads on a
                                     light profile background. -->
                                <span v-if="isPremium" class="user-premium-badge" title="Gamebot Premium">
                                    <img src="@/assets/images/premium/crown-white.svg" alt="Gamebot Premium" />
                                </span>
                                <!-- The count is announced, not just drawn:
                                     a red disc with a digit in it means
                                     nothing to a screen reader, and this is
                                     the only prompt to claim outside the
                                     profile itself. -->
                                <span
                                    v-if="unclaimedCount"
                                    class="unclaimed-badge"
                                    :aria-label="`${unclaimedCount} ${unclaimedCount === 1 ? 'reward' : 'rewards'} to claim`"
                                    role="status">{{ unclaimedLabel }}</span>
                            </span>
                        </b-button>
                    </p>
                    <p class="control">
                        <b-dropdown aria-role="list" class="user-actions" position="is-bottom-left" :mobile-modal="false">
                            <template #trigger>
                                <b-button
                                    icon-right="ellipsis-v"
                                    icon-pack="fas"
                                    type="is-discord"
                                    aria-label="Account options" />
                            </template>
                            <!-- Just the actions. Navigating to the profile is
                                 the name beside this button, not a duplicate
                                 entry in here.
                                 
                                 "Manage premium" used to sit above these. It
                                 was the only entry that opened someone else's
                                 site, and it was only ever there for
                                 subscribers, so the menu changed shape
                                 depending on who you were. It now lives at the
                                 bottom of the Customize tab, with the rest of
                                 what you own and control — which is where
                                 Settings goes. -->
                            <b-dropdown-item
                                aria-role="listitem"
                                has-link>
                                <router-link to="/profile/me/customize">Settings</router-link>
                            </b-dropdown-item>
                            <b-dropdown-item
                                class="has-background-danger has-text-white"
                                aria-role="listitem"
                                @click="$store.dispatch('logout')">
                                Log out
                            </b-dropdown-item>
                        </b-dropdown>
                    </p>
                </div>
            </b-navbar-item>
        </template>

        <template #burger>
            <div class="navbar-end is-burger">
            <a class="navbar-item">
                <nav-balance size="is-small" v-if="$store.getters.getToken && $route.path === '/shop'"  :credits="$store.state.dbInfo.credits" :gold="$store.state.dbInfo.gold" />

                <!-- The user chip is hidden at this width, so the badge has
                     to ride the burger instead — otherwise the one surface
                     that tells you a reward is waiting disappears on the
                     device most people play from. -->
                <a
                    role="button"
                    v-on:click="toggleSidebar"
                    :aria-label="unclaimedCount ? `menu — ${unclaimedCount} rewards to claim` : 'menu'"
                    class="navbar-burger burger"
                    :class="{ 'has-unclaimed': unclaimedCount }">
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
                <span v-if="unclaimedCount" class="unclaimed-badge is-on-burger" aria-hidden="true">{{ unclaimedLabel }}</span>
                </a>
            </a>
            </div>
        </template>
    </b-navbar>
</template>

<script>
import NavBalance from './NavBalance.vue'
export default {
  components: { NavBalance },
  name: 'NavBar',
  props: {
    activeRoute: String
  },
  data() {
      return {
      }
  },
  computed: {
    getUserTag() {
        let user = this.$store.state.user
        return `${user.username || ''}`
    },
    isPremium() {
        return this.$store.state.profile.premium === true
    },

    /**
     * How many levels have a reward waiting.
     *
     * From `dbInfo`, which App.vue loads on every page, rather than from the
     * reward track — that is fetched by the profile page alone, so a badge
     * reading it would be blank everywhere the badge is actually needed.
     * `fetchDBInfo` is also dispatched after every claim, so this clears
     * itself without the nav knowing claiming exists.
     */
    unclaimedCount() {
        const count = this.$store.state.dbInfo.unclaimedCount
        return typeof count === 'number' && count > 0 ? count : 0
    },

    /** Past this it stops being a count and starts being a shape. */
    unclaimedLabel() {
        return this.unclaimedCount > 9 ? '9+' : String(this.unclaimedCount)
    },
  },
  methods: {
    toggleSidebar() {
        this.$emit('toggleSidebar')
    },
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
nav.navbar {
    background-color: unset;
    max-width: $widescreen;
    margin: 0 auto;
}
nav.navbar .navbar-brand {
    margin-right: 15px;
    margin-top: -6px;
}

nav .navbar-burger {
  color: white;
}


a.navbar-item {
    padding: 0 8px;
}

// Premium's nav item, pilled.
//
// The pill is on an inner span rather than the .navbar-item itself: Bulma
// gives navbar items their own padding and hover background, and putting a
// border on the item would stretch the pill to the full height of the bar.
.premium-nav-item {
    // Bulma tints the item background on hover, which would show as a block
    // behind a rounded pill. The pill does the hover instead.
    &:hover,
    &.is-active,
    &.router-link-exact-active {
        background-color: transparent !important;
    }
}

.premium-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border: 1px solid $gold;
    border-radius: 999px;
    padding: 0.25rem 0.7rem;
    line-height: 1.2;
    color: $white;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    .premium-nav-item:hover &,
    .premium-nav-item.router-link-exact-active & {
        // Fills rather than brightening the text: white is already at the top
        // of its range, so there's nowhere for the label to go on hover.
        background-color: rgba($gold, 0.18);
        border-color: $gold-light;
    }
}

.premium-pill-crown {
    display: block;
    width: 0.85rem;
    height: auto;
}

.tag.is-small {
    font-size: 0.5rem;
    padding: 0.25rem 0.5rem;
    margin-left: 5px;
}

.user-actions {
    // Bulma's dropdown defaults to a 12rem minimum and padded content built
    // for a menu of several lines — for two short actions ("Settings", "Log
    // out") that's mostly empty margin around the text. Sized to the content
    // instead, and the hover state gets a transition rather than an instant
    // colour swap.
    ::v-deep .dropdown-menu {
        min-width: 0;
    }

    ::v-deep .dropdown-content {
        padding: 0.25rem 0;
    }

    // Left-aligned, on a shared left edge.
    //
    // Two selectors, and that split is why this looked crooked. A plain item
    // renders as `<a class="dropdown-item">`, but one with `has-link` renders
    // as `<div class="has-link"><a>` — Buefy styles that inner anchor
    // separately, with a `padding-right: 3rem` the plain item never gets. So
    // "Settings" carried 3rem of right padding against "Log out"'s 0.85rem,
    // and since the menu is right-aligned and sized to its content
    // (`min-width: 0` above), the two labels shared neither edge.
    //
    // Nested under `.dropdown-menu` to outspecify Buefy's own
    // `.dropdown .dropdown-menu .has-link a`, which this otherwise only ties
    // with — leaving it to bundle order to decide, which is not a thing to
    // depend on.
    //
    // Scoped to the items rather than set on the container, so the trigger
    // button is left out of it: `.user-actions` wraps `.dropdown-trigger` too.
    ::v-deep .dropdown-menu .dropdown-item,
    ::v-deep .dropdown-menu .has-link a {
        padding: 0.5rem 0.85rem;
        white-space: nowrap;
        text-align: left;
        transition: background-color 200ms ease-out;
    }
}

// `has-addons` does the joining (no gap, squared inner corners). Everything
// here is just the avatar and name sitting inside the existing button.
.user-menu {
    margin-bottom: 0;

    // Buefy/Bulma already style .button, including the hover and focus states
    // for is-discord. Rendering it with tag="router-link" makes it an <a>,
    // which is what dragged in the underline — everything here is just
    // undoing that, not restyling the button.
    .user-link {
        // Bulma pads a button symmetrically for a text label; an avatar on the
        // left needs a little less in front of it and a little more after, or
        // it reads as crowded against the edge.
        padding-left: 0.65rem;
        padding-right: 1rem;
        text-decoration: none;

        &:hover,
        &:focus,
        &:active {
            text-decoration: none;
        }
    }

    // Hairline between the two halves so the split target is legible.
    .control + .control .button {
        border-left: 1px solid rgba(255, 255, 255, 0.25);
    }
}

// The avatar and name are laid out here, one level below the button, because
// this is the element that actually contains both of them.
.user-link-inner {
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

.user-link-avatar {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    object-fit: cover;
    // An <img> is inline by default, so it sits on the text baseline and rides
    // a few pixels low however the button aligns its children. Block makes it
    // a flex item the button can actually centre.
    display: block;
    flex: 0 0 auto;
}

.user-link-name {
    display: block;
    line-height: 1;
    // Long Discord names shouldn't push the nav around.
    max-width: 10rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/*
 * The unclaimed-rewards count.
 *
 * Red on purpose, and the only red in the bar: this is the one thing here
 * asking you to go and do something. It sits beside the crown on the user
 * chip and rides the burger on a phone, where the chip is hidden — a badge
 * that vanished at the width most people play from would be pointing at
 * nothing.
 */
.unclaimed-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    // A circle at one digit and a pill at two, rather than an ellipse: the
    // min-width is the diameter and the padding only opens up past it.
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.3rem;
    border-radius: 999px;
    background-color: $danger;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    // The count is a number, so it should not jiggle as it changes.
    font-variant-numeric: tabular-nums;
}

.unclaimed-badge.is-on-burger {
    // The burger is a 3-bar glyph with no content box to sit next to, so this
    // is pinned to its corner instead. Bulma gives .navbar-burger
    // `position: relative` already; `.has-unclaimed` below is belt and braces
    // for the day that changes, since an unpositioned parent would fling this
    // to the top of the page rather than merely look wrong.
    position: absolute;
    top: 0.35rem;
    right: 0.2rem;
    // Small enough not to swallow the bars it sits over.
    min-width: 1rem;
    height: 1rem;
    font-size: 0.6rem;
    padding: 0 0.22rem;
    // Nothing here is clickable on its own — the whole burger is the target.
    pointer-events: none;
}

.navbar-burger.has-unclaimed {
    position: relative;
}

.user-premium-badge {
    display: flex;
    align-items: center;
    flex: 0 0 auto;

    img {
        width: 1.1rem;
        height: auto;
        display: block;
    }
}

a.navbar-item:focus-within, .navbar-item:focus { background-color: unset; }
.is-burger {
    margin-left: auto;
    @include tablet {
        display: none;
    }
}
</style>
